// Directive team performance evaluation.
// Two request types expose IDENTICAL evaluation schemas on different paths:
//   - Sector (accuracy):  accuracy_government_sector/directive-accuracy/evaluations[-prosecutor]/
//   - Register:           directive_registration/directive-evaluations[-prosecutor]/
// Both share field names (directive_request FK, work_*_evaluation, prosecutor_user_id, ...),
// so one factory builds the API for either path set.

const BASE = process.env.REACT_APP_API_BASE_URL;

export interface DirectiveEvaluationPayload {
    directive_request: string;
    evaluated_by?: string;
    evaluation_date?: string;
    work_speed_evaluation?: number;
    work_quality_evaluation?: number;
    work_load_evaluation?: number;
}

export interface DirectiveEvaluationProsecutorPayload {
    evaluation: string;
    prosecutor_user_id?: string;
    prosecutor_name?: string;
}

export interface DirectiveEvaluationApi {
    createEvaluation: (
        bearer: string,
        data: DirectiveEvaluationPayload
    ) => Promise<any>;
    updateEvaluation: (
        bearer: string,
        id: string,
        data: Partial<DirectiveEvaluationPayload>
    ) => Promise<any>;
    getEvaluations: (bearer: string, directiveRequestId?: string) => Promise<any>;
    createProsecutor: (
        bearer: string,
        data: DirectiveEvaluationProsecutorPayload
    ) => Promise<any>;
    updateProsecutor: (
        bearer: string,
        id: string,
        data: Partial<DirectiveEvaluationProsecutorPayload>
    ) => Promise<any>;
    getProsecutors: (bearer: string, evaluationId?: string) => Promise<any>;
}

const jsonHeaders = (bearer: string) => ({
    Authorization: `Bearer ${bearer}`,
    "Content-Type": "application/json",
});

/**
 * Build an evaluation API for a given path pair.
 * @param evalsPath e.g. "accuracy_government_sector/directive-accuracy/evaluations"
 * @param prosPath  e.g. "accuracy_government_sector/directive-accuracy/evaluations-prosecutor"
 */
export const makeDirectiveEvaluationApi = (
    evalsPath: string,
    prosPath: string
): DirectiveEvaluationApi => ({
    createEvaluation: async (bearer, data) => {
        const res = await fetch(`${BASE}${evalsPath}/`, {
            headers: jsonHeaders(bearer),
            method: "POST",
            body: JSON.stringify(data),
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to create directive evaluation");
        }
        return res.json();
    },
    updateEvaluation: async (bearer, id, data) => {
        const res = await fetch(`${BASE}${evalsPath}/${id}/`, {
            headers: jsonHeaders(bearer),
            method: "PATCH",
            body: JSON.stringify(data),
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to update directive evaluation");
        }
        return res.json();
    },
    getEvaluations: async (bearer, directiveRequestId) => {
        // Services name the filter differently; send both so the server
        // returns only this request's evaluations.
        const id = directiveRequestId ? encodeURIComponent(directiveRequestId) : "";
        const url = directiveRequestId
            ? `${BASE}${evalsPath}/?directive_request=${id}&directive_request_id=${id}`
            : `${BASE}${evalsPath}/`;
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${bearer}` },
            method: "GET",
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to get directive evaluations");
        }
        return res.json();
    },
    createProsecutor: async (bearer, data) => {
        const res = await fetch(`${BASE}${prosPath}/`, {
            headers: jsonHeaders(bearer),
            method: "POST",
            body: JSON.stringify(data),
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to create directive prosecutor evaluation");
        }
        return res.json();
    },
    updateProsecutor: async (bearer, id, data) => {
        const res = await fetch(`${BASE}${prosPath}/${id}/`, {
            headers: jsonHeaders(bearer),
            method: "PATCH",
            body: JSON.stringify(data),
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to update directive prosecutor evaluation");
        }
        return res.json();
    },
    getProsecutors: async (bearer, evaluationId) => {
        const url = evaluationId
            ? `${BASE}${prosPath}/?evaluation_id=${encodeURIComponent(evaluationId)}`
            : `${BASE}${prosPath}/`;
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${bearer}` },
            method: "GET",
        });
        if (!res.ok) {
            console.log(await res.text());
            throw new Error("Failed to get directive prosecutor evaluations");
        }
        return res.json();
    },
});

// Sector (Checking Accuracy of Sectors)
export const sectorEvaluationApi = makeDirectiveEvaluationApi(
    "accuracy_government_sector/directive-accuracy/evaluations",
    "accuracy_government_sector/directive-accuracy/evaluations-prosecutor"
);

// Register Directives
export const registerEvaluationApi = makeDirectiveEvaluationApi(
    "directive_registration/directive-evaluations",
    "directive_registration/directive-evaluations-prosecutor"
);

// Law Implementation Follow-up
export const lawImplementationEvaluationApi = makeDirectiveEvaluationApi(
    "law_implementation_follow_up/followup-evaluations",
    "law_implementation_follow_up/followup-evaluations-prosecutor"
);

// Legal Advisory
export const legalAdvisoryEvaluationApi = makeDirectiveEvaluationApi(
    "legal_advisory/legal-advisory-evaluations",
    "legal_advisory/legal-advisory-evaluations-prosecutor"
);

// Legal Awareness
export const legalAwarenessEvaluationApi = makeDirectiveEvaluationApi(
    "legal_awareness/legal-awareness-evaluations",
    "legal_awareness/legal-awareness-evaluations-prosecutor"
);

// Legal Research (external)
export const legalResearchEvaluationApi = makeDirectiveEvaluationApi(
    "legal_research/legal-research-evaluations",
    "legal_research/legal-research-evaluations-prosecutor"
);

// Legal Research (internal)
export const internalLegalResearchEvaluationApi = makeDirectiveEvaluationApi(
    "legal_research/internal-legal-research-evaluations",
    "legal_research/internal-legal-research-evaluations-prosecutor"
);

// Legal Training Provision
export const legalTrainingEvaluationApi = makeDirectiveEvaluationApi(
    "legal_training_provision/training-provision-evaluations",
    "legal_training_provision/training-provision-evaluations-prosecutor"
);

// Legal Translation
export const legalTranslationEvaluationApi = makeDirectiveEvaluationApi(
    "legal_translation/legal-translation-evaluations",
    "legal_translation/legal-translation-evaluations-prosecutor"
);

// Legislative Drafting
export const legislativeDraftingEvaluationApi = makeDirectiveEvaluationApi(
    "legislative_drafting/drafting-instruction-evaluations",
    "legislative_drafting/drafting-instruction-evaluations-prosecutor"
);
